/* Handles `value` and `defaultValue`.
 *
 * Strategy:
 * - Always use `value` if present.
 * - Behave as read-only when a constant `value` is present.
 * - `defaultValue` only takes effects when mounting. Changing `defaultValue` on the fly does nothing.
 * - Suspend control and track current state when `value` changes to `undefined`.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
export function useMixedValue(value, defaultValue, onChange, options) {
    const valueRef = useRef(value);
    const isSameValue = options?.isSameValue;
    const [innerValue, setInnerValue] = useState(() => {
        if (value !== undefined) {
            return value;
        }
        return defaultValue;
    });
    useLayoutEffect(() => {
        if (value !== undefined &&
            (valueRef.current === undefined ||
                !isSameValue?.(valueRef.current, value))) {
            setInnerValue(value);
        }
        valueRef.current = value;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    const onUpdate = useCallback((targetValue, ...args) => {
        onChange?.(targetValue, ...args);
        if (valueRef.current === undefined) {
            setInnerValue(targetValue);
        }
    }, [onChange]);
    // value can be null here, and we consider this not to be an empty value
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const mergedValue = value !== undefined ? value : innerValue;
    return [mergedValue, onUpdate];
}
